import type { ExpertProfileFilters } from '../types/expert-profile';
import type { ExpertTipFilters } from '../types/expert-tip';
import type { EarlyAccessFilters } from '../types/early-access';

/**
 * Query keys factory for React Query
 * Provides a consistent, hierarchical structure for cache keys
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
  },
  expertProfiles: {
    all: ['expertProfiles'] as const,
    lists: () => [...queryKeys.expertProfiles.all, 'list'] as const,
    list: (filters: Partial<ExpertProfileFilters>) =>
      [...queryKeys.expertProfiles.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.expertProfiles.all, 'detail', id] as const,
    active: () => [...queryKeys.expertProfiles.all, 'active'] as const,
  },
  expertTips: {
    all: ['expertTips'] as const,
    lists: () => [...queryKeys.expertTips.all, 'list'] as const,
    list: (filters: Partial<ExpertTipFilters>) =>
      [...queryKeys.expertTips.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.expertTips.all, 'detail', id] as const,
    filterOptions: () => [...queryKeys.expertTips.all, 'filterOptions'] as const,
  },

  adminUsers: {
    all: ['adminUsers'] as const,
    lists: () => [...queryKeys.adminUsers.all, 'list'] as const,
    list: () => [...queryKeys.adminUsers.lists()] as const,
    detail: (id: string) => [...queryKeys.adminUsers.all, 'detail', id] as const,
  },
  adminRecommendations: {
    all: ['adminRecommendations'] as const,
    child: (childId: number) => [...queryKeys.adminRecommendations.all, 'child', childId] as const,
  },
  earlyAccessSignups: {
    all: ['earlyAccessSignups'] as const,
    lists: () => [...queryKeys.earlyAccessSignups.all, 'list'] as const,
    list: (filters: Partial<EarlyAccessFilters>) =>
      [...queryKeys.earlyAccessSignups.lists(), filters] as const,
  },
  communicationStages: {
    all: ['communicationStages'] as const,
    list: () => [...queryKeys.communicationStages.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.communicationStages.all, 'detail', id] as const,
  },
  domains: {
    all: ['domains'] as const,
    list: () => [...queryKeys.domains.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.domains.all, 'detail', id] as const,
  },
  domainSkills: {
    all: ['domainSkills'] as const,
    lists: () => [...queryKeys.domainSkills.all, 'list'] as const,
    list: (filters?: { domainId?: number }) =>
      [...queryKeys.domainSkills.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.domainSkills.all, 'detail', id] as const,
  },
  childPreferences: {
    all: ['childPreferences'] as const,
    list: () => [...queryKeys.childPreferences.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.childPreferences.all, 'detail', id] as const,
  },
  sparkPlays: {
    all: ['sparkPlays'] as const,
    lists: () => [...queryKeys.sparkPlays.all, 'list'] as const,
    list: () => [...queryKeys.sparkPlays.lists()] as const,
    detail: (id: number) => [...queryKeys.sparkPlays.all, 'detail', id] as const,
  },
  activityPreps: {
    all: ['activityPreps'] as const,
    lists: () => [...queryKeys.activityPreps.all, 'list'] as const,
    list: () => [...queryKeys.activityPreps.lists()] as const,
    detail: (id: number) => [...queryKeys.activityPreps.all, 'detail', id] as const,
  },
  packNames: {
    all: ['packNames'] as const,
    list: () => [...queryKeys.packNames.all, 'list'] as const,
  },
  sparkActivities: {
    all: ['sparkActivities'] as const,
    lists: () => [...queryKeys.sparkActivities.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.sparkActivities.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.sparkActivities.all, 'detail', id] as const,
    batches: () => [...queryKeys.sparkActivities.all, 'batches'] as const,
    filters: () => [...queryKeys.sparkActivities.all, 'filters'] as const,
    checkKey: (key: string) => [...queryKeys.sparkActivities.all, 'checkKey', key] as const,
  },
  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.activities.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.activities.all, 'detail', id] as const,
    filters: () => [...queryKeys.activities.all, 'filters'] as const,
    overview: (status: string) => [...queryKeys.activities.all, 'overview', status] as const,
  },
  playTypes: {
    all: ['playTypes'] as const,
    list: () => [...queryKeys.playTypes.all, 'list'] as const,
  },
  programme: {
    all: ['programme'] as const,
    overview: () => [...queryKeys.programme.all, 'overview'] as const,
  },
  testUsers: {
    all: ['testUsers'] as const,
    list: () => [...queryKeys.testUsers.all, 'list'] as const,
  },
} as const;
